<!--
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
-->
<!-- IP-NOTICE: CUSTOM -->
<section class="orbit-table" [attr.aria-label]="config.ariaLabel" [attr.data-orbit-dashboard]="config.dashboard">
	<div *ngIf="config.listActions?.length" class="orbit-table__list-actions" aria-label="List actions">
		<ng-container *ngFor="let action of config.listActions; trackBy: trackByAction">
			<button *ngIf="actionVisible(action) && action.variant === 'primary'" mat-flat-button type="button" class="orbit-button orbit-button--primary orbit-button--medium orbit-table__list-action"
				[disabled]="actionDisabled(action)" [attr.aria-label]="actionAriaLabel(action)"
				[matTooltip]="actionTooltip(action)" (click)="invokeListAction(action, $event)">
				<orbit-icon [name]="action.icon"></orbit-icon><span>{{ action.label }}</span>
			</button>
			<button *ngIf="actionVisible(action) && action.variant !== 'primary'" mat-stroked-button type="button" class="orbit-button orbit-button--secondary orbit-button--medium orbit-table__list-action"
				[disabled]="actionDisabled(action)" [attr.aria-label]="actionAriaLabel(action)"
				[matTooltip]="actionTooltip(action)" (click)="invokeListAction(action, $event)">
				<orbit-icon [name]="action.icon"></orbit-icon><span>{{ action.label }}</span>
			</button>
		</ng-container>
	</div>

	<ng-container *ngIf="filterTemplate; else projectedFilter" [ngTemplateOutlet]="filterTemplate"></ng-container>
	<ng-template #projectedFilter><ng-content select="[orbitTableFilter]"></ng-content></ng-template>

	<div *ngIf="config.resultSummary?.visible" class="orbit-table__results">
		<span class="orbit-table__results-count" aria-live="polite" aria-atomic="true">
			<ng-container *ngIf="loading">UPDATING RESULTS...</ng-container>
			<ng-container *ngIf="!loading && error">RESULTS UNAVAILABLE</ng-container>
			<ng-container *ngIf="!loading && !error && totalCount !== null">SHOWING {{ currentResultCount }} OF {{ totalCount }} {{ resultEntityLabel.toUpperCase() }}</ng-container>
		</span>
		<span *ngIf="config.resultSummary.hint" class="orbit-table__hint">{{ config.resultSummary.hint }}</span>
	</div>

	<div class="orbit-table__card" [class.orbit-table__card--paginated]="showPaginator" [style.max-height]="tableMaxHeight">
		<table mat-table [dataSource]="displayedRows" [trackBy]="trackByRow" matSort cdkDropList
			[cdkDropListData]="displayedRows" [cdkDropListDisabled]="!config.reordering?.enabled"
			(cdkDropListDropped)="onRowDropped($event)"
			[matSortActive]="sortActive" [matSortDirection]="sortDirection"
			[matSortDisableClear]="config.sorting?.disableClear !== false" (matSortChange)="onSortChanged($event)"
			[style.min-width]="config.scroll?.minWidth">
			<ng-container *ngFor="let column of config.columns; let columnIndex = index; trackBy: trackByColumn" [matColumnDef]="column.key" [sticky]="isSticky(column, columnIndex)">
				<th mat-header-cell *matHeaderCellDef [style.width]="column.width" [class]="column.headerClass || ''"
					[class.orbit-table__cell--sticky]="isSticky(column, columnIndex)" [class.orbit-table__cell--sticky-header]="isSticky(column, columnIndex)"
					[class.orbit-table__cell--center]="column.align === 'center'" [class.orbit-table__cell--right]="column.align === 'right'"
					[mat-sort-header]="column.sortField || column.key" [arrowPosition]="column.align === 'right' ? 'before' : 'after'"
					[disabled]="!isSortable(column)" [attr.aria-label]="column.headerAriaLabel || null">
					<div class="orbit-table__header-content"
						[class.orbit-table__header-content--center]="column.align === 'center'"
						[class.orbit-table__header-content--right]="column.align === 'right'">
						<ng-container *ngIf="headerTemplate(column.key) as template; else defaultHeader" [ngTemplateOutlet]="template" [ngTemplateOutletContext]="{ $implicit: column, column: column }"></ng-container>
						<ng-template #defaultHeader>{{ column.label }}</ng-template>
					</div>
				</th>
				<td mat-cell *matCellDef="let row" [style.width]="column.width" [class]="cellClass(column, row)"
					[class.orbit-table__cell--sticky]="isSticky(column, columnIndex)" [class.orbit-table__cell--sticky-body]="isSticky(column, columnIndex)"
					[class.orbit-table__cell--center]="column.align === 'center'" [class.orbit-table__cell--right]="column.align === 'right'">
					<div class="orbit-table__cell-content" [class.orbit-table__cell-content--truncate]="column.truncate !== false"
						[matTooltip]="tooltipFor(column, row)" [matTooltipDisabled]="!tooltipFor(column, row)">
						<ng-container *ngIf="cellTemplate(column.key) as template; else defaultCell" [ngTemplateOutlet]="template" [ngTemplateOutletContext]="{ $implicit: row, row: row, value: columnValue(column, row) }"></ng-container>
						<ng-template #defaultCell>{{ displayValue(column, row) }}</ng-template>
					</div>
				</td>
				<td mat-footer-cell *matFooterCellDef [style.width]="column.width" [class]="column.footerClass || ''"
					[class.orbit-table__cell--sticky]="isSticky(column, columnIndex)"
					[class.orbit-table__cell--right]="column.align === 'right'"
					[class.orbit-table__cell--center]="column.align === 'center'">
					{{ footerDisplayValue(column) }}
				</td>
			</ng-container>

			<ng-container *ngIf="config.rowActions?.length" matColumnDef="__actions" stickyEnd>
				<th mat-header-cell *matHeaderCellDef class="orbit-table__actions-header orbit-table__cell--sticky orbit-table__cell--sticky-header orbit-table__cell--sticky-end" aria-label="Actions"
					[style.width]="actionsColumnWidth" [style.min-width]="actionsColumnWidth" [style.max-width]="actionsColumnWidth">
					<span class="orbit-table__sr-only">Actions</span>
					<span class="orbit-table__actions-header-spacer" aria-hidden="true">
						<span *ngFor="let action of config.rowActions; trackBy: trackByAction" class="orbit-table__actions-header-action"></span>
					</span>
				</th>
				<td mat-cell *matCellDef="let row" class="orbit-table__actions-cell orbit-table__cell--sticky orbit-table__cell--sticky-body orbit-table__cell--sticky-end"
					[style.width]="actionsColumnWidth" [style.min-width]="actionsColumnWidth" [style.max-width]="actionsColumnWidth">
					<div class="orbit-table__row-actions" [class.orbit-table__row-actions--always]="config.rowActionsVisibility === 'always'">
						<ng-container *ngFor="let action of config.rowActions; trackBy: trackByAction">
							<button *ngIf="actionVisible(action, row)" mat-icon-button type="button" class="orbit-row-action orbit-table__row-action"
								[disabled]="actionDisabled(action, row)" [attr.aria-label]="actionAriaLabel(action, row)"
								[matTooltip]="actionTooltip(action, row)" (click)="invokeRowAction(action, row, $event)">
								<orbit-icon [name]="action.icon"></orbit-icon>
							</button>
						</ng-container>
					</div>
				</td>
				<td mat-footer-cell *matFooterCellDef class="orbit-table__actions-cell orbit-table__cell--sticky orbit-table__cell--sticky-end"
					[style.width]="actionsColumnWidth" [style.min-width]="actionsColumnWidth" [style.max-width]="actionsColumnWidth"></td>
			</ng-container>

			<tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: config.scroll?.stickyHeader !== false"></tr>
			<tr mat-row cdkDrag [cdkDragData]="row" [cdkDragDisabled]="!config.reordering?.enabled"
				*matRowDef="let row; columns: displayedColumns" [ngClass]="rowClasses(row)"
				[attr.tabindex]="isSelectable() ? 0 : null" [attr.aria-selected]="isSelectable() ? isSelected(row) : null"
				(click)="onRowClicked(row, $event)" (dblclick)="onRowDoubleClicked(row, $event)" (keydown)="onRowKeydown(row, $event)"></tr>
			<tr mat-footer-row *matFooterRowDef="displayedColumns; sticky: config.footer?.sticky === true"
				[class.orbit-table__footer-row--hidden]="!showFooter" [attr.aria-hidden]="!showFooter"></tr>
		</table>

		<div *ngIf="loading" class="orbit-table__state orbit-table__state--overlay" role="status">
			<mat-spinner diameter="28"></mat-spinner><span>{{ config.state?.loadingMessage || 'Loading results' }}</span>
		</div>
		<div *ngIf="!loading && error" class="orbit-table__state" role="alert">{{ config.state?.errorMessage || 'Results could not be loaded.' }}</div>
		<div *ngIf="!loading && !error && !displayedRows.length" class="orbit-table__state orbit-table__empty">
			<h2>{{ config.state?.emptyTitle || 'No results' }}</h2>
			<p>{{ config.state?.emptyMessage || 'Try changing or clearing the filters.' }}</p>
		</div>
	</div>

	<div *ngIf="showPaginator" class="orbit-table-paginator orbit-paginator-nav">
		<mat-paginator [length]="paginatorLength" [pageIndex]="pageIndex"
			[pageSize]="pageSize" [pageSizeOptions]="config.pagination.pageSizeOptions || [10, 20, 50]"
			[selectConfig]="paginatorSelectConfig"
			[showFirstLastButtons]="config.pagination.showFirstLastButtons !== false" (page)="onPageChanged($event)">
		</mat-paginator>
	</div>
</section>
